import { ERROR_MESSAGES } from 'constants/messages';
import { Resources } from 'constants/resources';

import { useMemo } from 'react';

import { SubmissionErrors, hydraSchemaAnalyzer } from '@api-platform/admin';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigateToRoot } from 'hooks/useNavigateToRoot';
import { GatewaysFormSkeleton } from 'pages/Gateways/components/GatewaysFormSkeleton';
import { gatewayEditSchema } from 'pages/Gateways/schemas/schema';
import { Form, HttpError, useEditController, useNotify, useUpdate } from 'react-admin';
import { FieldValues } from 'react-hook-form';
import { EditPage } from 'shared/react-admin/Pages/EditPage/EditPage';
import { Gateway } from 'types/embedded/gateway';

import { GatewaysEditForm } from './GatewaysEditForm';
import { useGatewayPilotModeSchedule } from './hooks/useGatewayPilotModeSchedule';
import { GatewayEditFormValues } from './types';
import { getGatewayFormDefaultValues } from './utils/getGatewayFormDefaultValues';
import { prepareGatewayEditSubmitValues } from './utils/prepareGatewayEditSubmitValues';

export const GatewaysEdit: React.FC = () => {
  const notify = useNotify();
  const { toRoot } = useNavigateToRoot();
  const { record } = useEditController<Gateway>();
  const [update] = useUpdate();

  const { gatewayPilotModeSchedule, isScheduleLoading } = useGatewayPilotModeSchedule(record);

  const formDefaultValues = useMemo(
    () => getGatewayFormDefaultValues(record, gatewayPilotModeSchedule),
    [record, gatewayPilotModeSchedule],
  );

  const isLoading = !record || isScheduleLoading;

  async function onSubmit(formValues: FieldValues): Promise<SubmissionErrors | void> {
    if (!record) {
      return;
    }

    try {
      await update(
        Resources.Gateways,
        {
          id: record.id,
          data: prepareGatewayEditSubmitValues(formValues as GatewayEditFormValues),
          previousData: record,
        },
        { returnPromise: true },
      );
      notify('Шлюз отредактирован!', { type: 'info' });
      toRoot();
    } catch (error) {
      notify(ERROR_MESSAGES.INVALID_FORM, { type: 'error' });
      return hydraSchemaAnalyzer().getSubmissionErrors(error as HttpError) || undefined;
    }
  }

  return (
    <EditPage listName="Шлюзы" title="Редактирование шлюза">
      {isLoading ? (
        <GatewaysFormSkeleton />
      ) : (
        <Form
          mode="onBlur"
          onSubmit={onSubmit}
          record={formDefaultValues}
          resolver={yupResolver<FieldValues>(gatewayEditSchema)}
        >
          <GatewaysEditForm />
        </Form>
      )}
    </EditPage>
  );
};
